import type { IQService, ITimeoutService, IDeferred } from 'angular';

import type { ExternalServiceManager } from './external-service-manager';
import type { HttpService } from '../../../App/services/http-service';
import type { EventService } from '../../../App/services/event-service';
import { EventCodes } from '../../../App/model/event-codes';
import { WithRequired } from '../../../Types/WithRequired';
import { FilterExpressionRequestParams, IFilterExpression } from '../model/filterExpressionUtils';

export type FragmentDescriptorRequest = {
	entityClass: string;
	filter?: IFilterExpression;
	top?: number;
	allowedTypes?: string;
	exclude?: string;
	ids?: string[];
	objectId?: string;
	fragmentId?: string;
	parent?: string;
	rootElement?: string;
	includeRoot?: boolean;
	relationName?: string;
	useObjectId?: boolean;
	displayExpression?: string;
	notStrictAutocomplete?: boolean;
	archived?: boolean;
	dataQueryName?: string;
};

export type FragmentDescriptorPostRequest = WithRequired<FragmentDescriptorRequest, 'ids'>;

export type FragmentDescriptorRequestExtraResult = {
	EntityClass?: string;
};

export type FragmentDescriptor = {
	Id: string;
	ObjectId: string;
	DisplayString: string;
	EntityType: string;

	DisplayExpression?: string; //TODO: is it legacy?
	ParentDisplayString?: string;
	Parents?: FragmentDescriptor[];
};

export type ObjectPreprocessorParams = {
	EntityType: string;
	Id: string;
	IsArchived?: boolean;
};

export type EntityContext = {
	_id: string;
	_type: string;
	_name?: string;
	_state?: string;
	_displayName: string;
	_archived?: boolean;
};

export type ObjectPreprocessor = (params: ObjectPreprocessorParams) => Promise<ObjectPreprocessorParams>;

export type FragmentsApiResult = {
	EntityClass: string;
	Fragments: FragmentDescriptor[];
};

type FragmentRequestPromise = {
	Id: string;
	DisplayExpression: string;
	Promise: IDeferred<FragmentDescriptor>;
};

export type EnumerationValue = {
	DisplayString: string;
	Image: string;
	Position: number;
	StateGroup: number;
	Value: number;
	Hidden: boolean;
};

export type Enumeration = {
	Name: string;
	Values: EnumerationValue[] | null;
};

type EnumerationCache = {
	enums: Record<string, Promise<Enumeration>>;
	typePermissions: Record<string, unknown>;
};

export class EntityService {
	static $inject = ['$q', 'mx.SolutionBuilderAgent.Http', 'mx.SolutionBuilderAgent.ExternalServiceManager', '$timeout', 'mx.shell.EventService'];
	private readonly entityServiceUrl = 'api/entity/';
	private readonly _defaultKey = '*';
	private _itemsRequest: Map<string, FragmentDescriptorPostRequest> | null = null;
	private readonly _pendingPromise = new Map<string, FragmentRequestPromise[]>();
	private readonly _objectParameterProcessors: ObjectPreprocessor[] = [];
	private readonly cache: EnumerationCache = {
		enums: {},
		typePermissions: {},
	};

	constructor(
		private readonly $q: IQService,
		private readonly http: HttpService,
		private readonly externalServiceManager: ReturnType<typeof ExternalServiceManager>,
		private readonly $timeout: ITimeoutService,
		private readonly eventService: EventService
	) {
		eventService.subscribeGlobal(EventCodes.ActionsContextChanged, (eventObj, context) => {
			this._pendingPromise.delete(context?.widgetId || this._defaultKey);
		});

		/* properties for calc-fields */
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		window.mx.Schema = {
			getPickup: (name: string, filter: string) => this.getEnumerationValues(name, filter),
		};
		/* end */
	}

	private mapFragmentRequest(requestOptions: FragmentDescriptorRequest & { searchFilter?: string }) {
		const { ids, filter: filterExpression, ...otherOptions } = requestOptions;

		const filterRequestParams: FilterExpressionRequestParams & { filter?: string; WhereExpression?: string } = filterExpression?.toRequest() || {};
		if ('WhereExpression' in filterRequestParams) {
			filterRequestParams.filter = filterRequestParams.WhereExpression;
			delete filterRequestParams.WhereExpression;
		}

		return { ids, ...filterRequestParams, ...otherOptions };
	}

	private async getPostFragments(requestOptions: FragmentDescriptorPostRequest[]): Promise<FragmentDescriptor[]> {
		return await this.http.post(`${this.entityServiceUrl}fragments`, requestOptions);
	}

	private async getFragments(requestOptions: FragmentDescriptorRequest): Promise<FragmentsApiResult> {
		return await this.http.post(`${this.entityServiceUrl}fragments/forControl`, this.mapFragmentRequest(requestOptions));
	}

	/**
	 * Gets the details about Entity fragment. To avoid multiple Server requests functions accumulates the calls
	 * received within half a second, and send them in batch.
	 */
	public async getFragmentDescriptors(
		request: FragmentDescriptorRequest,
		searchText: string,
		extraResult: FragmentDescriptorRequestExtraResult = {}
	): Promise<FragmentDescriptor[]> {
		if (this.externalServiceManager.isService(request.entityClass)) {
			return this.externalServiceManager.getFragmentDescriptors(request, searchText);
		}

		const data = await this.getFragments(Object.assign({ searchFilter: searchText }, request));

		if (extraResult) {
			extraResult.EntityClass = data.EntityClass;
		}

		return data.Fragments;
	}

	/**
	 * Register a processor of object parameters
	 *
	 * @param {function} processor - A function-processor with one input Object parameter:
	 * {
	 * 		EntityType: string,
	 *		Id: Guid,
	 *		IsArchived: boolean
	 * }
	 * Processor must return {Object} - Normalized object description
	 */
	public registerObjectParameterProcessor(processor: ObjectPreprocessor): void {
		if (typeof processor === 'function') {
			this._objectParameterProcessors.push(processor);
		}
	}

	/**
	 * Normalizes object parameters for any purpose needed. Chain overridable method.
	 * @returns Normalized object description
	 */
	public async processObjectContext(context: EntityContext): Promise<EntityContext> {
		let result: ObjectPreprocessorParams = {
			EntityType: context._type,
			Id: context._id,
			IsArchived: context._archived,
		};
		for (let i = 0; i < this._objectParameterProcessors.length; i++) {
			try {
				result = await this._objectParameterProcessors[i](result);
			} catch (e) {
				console.error(e);
			}
		}

		if (context._type !== result.EntityType) {
			this.eventService.dispatchEvent(EventCodes.ObjectTypeChanged, {
				objectId: context._id,
				entityType: context._type,
				newEntityType: result.EntityType,
			});
		}

		if (typeof result.Id !== 'undefined') {
			context._id = result.Id;
		}

		if (typeof result.EntityType !== 'undefined') {
			context._type = result.EntityType;
		}

		return context;
	}

	private addPendingPromise(key: string, entityId: string, displayExpression: string): IDeferred<FragmentDescriptor> {
		key = key || this._defaultKey;

		let promiseForKey = this._pendingPromise.get(key);
		if (!promiseForKey) {
			promiseForKey = [];
			this._pendingPromise.set(key, promiseForKey);
		}

		let foundDefer = promiseForKey.find(({ Id, DisplayExpression }) => Id === entityId && DisplayExpression === displayExpression)?.Promise;

		if (!foundDefer) {
			foundDefer = this.$q.defer<FragmentDescriptor>();

			const newPromise = {
				Id: entityId,
				DisplayExpression: displayExpression,
				Promise: foundDefer,
			};

			promiseForKey.push(newPromise);
		}

		return foundDefer;
	}

	/**
	 * Gets the details about Entity fragment. To avoid multiple Server requests functions accumulates the calls
	 * received within half a second, and send them in batch.
	 *
	 * @param {string} entityClassName - Name of the Entity class.
	 * @param {string} id - id of the Entity
	 * @param {string} parentExpression - Specifies the Data Expression to obtain an extra info about fragment .
	 * @param {string} useObjectId - Use objectId instead ClassId.
	 * @param {string} rootElement -
	 * @param {boolean} includeRoot -
	 * @param {string} [widgetId] - ID of widget with control which calls the method, used for response caching.
	 * @param {string} [displayExpression] - A-SQL display expression for the result fragments.
	 * @param {string} [dataQueryName] - Data Query name
	 *
	 * @returns {FragmentDescriptor} item
	 */
	public async getFragmentDescriptorsInBatch(
		entityClassName: string,
		id: string,
		parentExpression: string,
		useObjectId: boolean,
		rootElement: string,
		includeRoot: boolean,
		widgetId: string,
		displayExpression: string,
		dataQueryName: string
	): Promise<null | FragmentDescriptor> {
		if (!id || !entityClassName) {
			return null;
		}

		const entityId = typeof id === 'string' ? id.toLowerCase() : id;

		const entityClass = (entityClassName || '').toLowerCase();
		const reqKey = entityClass + (displayExpression ? displayExpression.toLowerCase() : '') + '_' + useObjectId;

		if (!this._itemsRequest) {
			this._itemsRequest = new Map<string, WithRequired<FragmentDescriptorRequest, 'ids'>>();

			this.$timeout(async () => {
				const requestData = Array.from(this._itemsRequest || [], ([, value]) => value);

				const data = await this.getPostFragments(requestData);

				this._pendingPromise.forEach((values) => {
					values.forEach((pendingPromise) => {
						const foundItem = data.find((dataItem) => {
							return (
								(dataItem.Id.toLowerCase() === pendingPromise.Id || dataItem.ObjectId?.toLowerCase() === pendingPromise.Id) &&
								(dataItem.DisplayExpression || '') === (pendingPromise.DisplayExpression || '')
							);
						});

						if (foundItem) {
							pendingPromise.Promise.resolve(foundItem);
						}
					});
				});

				this._itemsRequest = null;
			}, 500);
		}

		const defer = this.addPendingPromise(widgetId, entityId, displayExpression);

		let classIds = this._itemsRequest.get(reqKey);

		if (!classIds) {
			classIds = {
				entityClass: entityClassName,
				displayExpression,
				ids: [],
				dataQueryName,
			};
			this._itemsRequest.set(reqKey, classIds);
		}

		if (parentExpression) {
			classIds.parent = parentExpression;
			classIds.rootElement = rootElement;
			classIds.includeRoot = includeRoot;
		}

		if (useObjectId) {
			classIds.useObjectId = useObjectId;
		}

		if (!classIds.ids?.includes(entityId)) {
			classIds.ids.push(entityId);
		}

		return defer.promise;
	}

	/**
	 * Check uniqueness of the Entity class attribute value. Validates there is no other value in specified
	 * ´entityClass´ table in column ´attributeName´, except the record with provided ´objectId´
	 *
	 * @param {string} entityClass - Name of the Entity class.
	 * @param {string} attributeName - Name of the Entity attribute.
	 * @param {string} objectId - Id of the object.
	 * @param {string} attrValue - Attribute value to validate.
	 * @param {string} entityType - Name of Entity Type of the edited object.
	 *
	 * @returns {boolean} Indicates that the checked value is unique
	 */
	public async ensureUniqueValue(entityClass: string, attributeName: string, objectId: string, attrValue: string, entityType: string): Promise<boolean> {
		return this.http.get(this.entityServiceUrl + 'ensureUnique', {
			attributeName: attributeName,
			entityClass: entityClass,
			objectId: objectId,
			value: attrValue,
			entityType: entityType,
		});
	}

	private async loadEnumeration(name: string): Promise<Enumeration> {
		try {
			return this.externalServiceManager.isService(name) ? this.externalServiceManager.getEnumeration(name) : this.http.get(this.entityServiceUrl + 'enumeration/' + name);
		} catch (e) {
			return {
				Name: name,
				Values: null,
			};
		}
	}

	public getEnumeration<ExtendedEnumeration extends Enumeration = Enumeration>(name: string): Promise<ExtendedEnumeration> {
		this.cache.enums[name] = this.cache.enums[name] || this.loadEnumeration(name);
		return this.cache.enums[name] as Promise<ExtendedEnumeration>;
	}

	private filterFunction(values: EnumerationValue[] | null, filter?: string) {
		if (!values) {
			return [];
		}

		if (!filter) {
			return values.slice();
		}

		const fnEval = (str: string) => eval(str);

		const userExpression = filter
			.replace(/\sand\s/gi, ' && ')
			.replace(/\sor\s/gi, ' || ')
			.replace(/<>/gi, '!=')
			.replace(/([^=])=([^=])/gi, (ex, g1, g2) => `${g1}==${g2}`);

		const result: EnumerationValue[] = [];
		values.forEach((value) => {
			let evalExpression = '';

			Object.entries(value).forEach(([key, val]) => {
				if (typeof val === 'number') {
					evalExpression += 'var ' + key + '=' + val + '; var ' + key.toLowerCase() + '=' + key + ';';
				}
			});
			evalExpression += userExpression;
			evalExpression += ';';
			if (fnEval.call({}, evalExpression)) {
				result.push(value);
			}
		});
		return result;
	}

	public async getEnumerationValues(name: string, filter?: string) {
		const data = await this.getEnumeration(name);
		return this.filterFunction(data.Values, filter);
	}

	/**
	 * Update some properties of Entity
	 *
	 * @param {string} entityType - EntityType.
	 * @param {string} objectId - Id of the object.
	 * @param {string} entityClassName - base EntityClassName of the Object.
	 * @param {string} values - Array of properties which will be updated. Format:
	 * 			[{
	 * 				Expression: 'Title',
	 * 				Value: 'New awesome title of the Object'
	 * 			}]
	 *
	 * @returns {promise} Result from the server
	 */
	public updateEntity(entityType: string, objectId: string, entityClassName: string, values: { Expression: string; Value: string }[]): Promise<void> {
		return this.http.post('api/data', {
			EntityName: entityType,
			ObjectId: objectId,
			ExpressionBaseClassName: entityClassName,
			Values: values,
		});
	}

	/**
	 * Get Type name an object belongs to.
	 *
	 * @param {string} objectId - id of the object.
	 *
	 * @returns {promise} Result from the server
	 */
	public async getObjectTypeName(objectId: string): Promise<string | null> {
		if (objectId) {
			return this.http.get('api/data/objectTypeName/' + objectId);
		}

		return null;
	}
}
